/**
 * Microphone audio as the listening socket takes it: 16 kHz, mono, signed
 * 16-bit little-endian, in 100 ms frames.
 *
 * The AudioContext runs at the device's own rate (48 or 44.1 kHz) and this
 * brings it down, rather than asking the context for 16 kHz: Firefox refuses
 * a microphone whose rate differs from its context's.
 */

export const TARGET_RATE = 16_000;
/** 100 ms at 16 kHz. */
export const FRAME_SAMPLES = 1_600;

/**
 * Down to 16 kHz by averaging each output sample's share of the input. A box
 * filter is a weak anti-alias filter, and speech recognition does not mind.
 * Keeps its place across calls, so chunk edges leave no seam.
 */
export class Resampler {
  private readonly step: number;
  private edge: number;
  private seen = 0;
  private sum = 0;
  private count = 0;

  constructor(inRate: number, outRate = TARGET_RATE) {
    this.step = inRate / outRate;
    this.edge = this.step;
  }

  push(input: Float32Array): Float32Array {
    if (this.step <= 1) return input.slice();
    const out = new Float32Array(Math.ceil(input.length / this.step) + 1);
    let n = 0;
    for (let i = 0; i < input.length; i++) {
      this.sum += input[i]!;
      this.count++;
      this.seen++;
      if (this.seen >= this.edge) {
        out[n++] = this.sum / this.count;
        this.sum = 0;
        this.count = 0;
        this.edge += this.step;
      }
    }
    return out.subarray(0, n);
  }
}

export function toInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

/** Gathers samples into whole frames; `flush` hands over what is left. */
export class Framer {
  private buf: Int16Array;
  private n = 0;

  constructor(private readonly size = FRAME_SAMPLES) {
    this.buf = new Int16Array(size);
  }

  push(samples: Int16Array): ArrayBuffer[] {
    const frames: ArrayBuffer[] = [];
    let i = 0;
    while (i < samples.length) {
      const take = Math.min(this.size - this.n, samples.length - i);
      this.buf.set(samples.subarray(i, i + take), this.n);
      this.n += take;
      i += take;
      if (this.n === this.size) {
        frames.push(this.buf.slice().buffer);
        this.n = 0;
      }
    }
    return frames;
  }

  flush(): ArrayBuffer | null {
    if (!this.n) return null;
    const last = this.buf.slice(0, this.n).buffer;
    this.n = 0;
    return last;
  }
}

/** How loud a chunk is, 0 to 1, for the level a voice orb follows. */
export function level(input: Float32Array): number {
  if (!input.length) return 0;
  let sum = 0;
  for (let i = 0; i < input.length; i++) sum += input[i]! * input[i]!;
  return Math.min(1, Math.sqrt(sum / input.length) * 4);
}
